/**
 * Sample data for `?showcase=dialogue` (dev only). Spoken lines are verbatim story.txt quotes (P2, P9,
 * P11, P12 openings); lines that only exist to exercise a mechanic are prefixed "[dev]" and reveal
 * nothing beyond the chapter. Speaker ids are the characters module's actor keys.
 *
 * Flags: the showcase state starts (and resets) at TITLE, like a new game, so both demos legally set
 * LETTER_OPEN (the dialogue's end node and the timeline's terminal step); re-running one is a silent no-op.
 */
import type { DialogueData, TimelineData } from '@/core/types/contracts';

export const ZICHAO = 'jiang-zichao';
export const ZHAO = 'zhao-chengbo';

export const SHOWCASE_DIALOGUES: DialogueData[] = [
  {
    id: 'showcase-branch', start: 'a',
    nodes: [
      { id: 'a', speaker: ZICHAO, text: '老师，你在想什么呢？', next: 'b', face: { actor: ZICHAO, facing: 'right' } },
      { id: 'b', speaker: ZHAO, text: '没想什么，走吧，我们要尽早找到旱龟蛋。', condition: { notMark: 'talked:once' }, setMark: 'talked:once', elseNext: 'b2', next: 'c', face: { actor: ZHAO, facing: 'left' } },
      { id: 'b2', speaker: ZHAO, text: '[dev] 已对话过（notMark 分支）：走吧。', next: 'c', emote: { actor: ZHAO, emote: 'think' } },
      { id: 'c', speaker: ZHAO, text: '我们先去找个地方歇歇，顺便问问那里的人。一会儿就下雨了。', condition: { weather: ['drizzle', 'rain'] }, elseNext: 'd', next: 'd', face: { actor: ZHAO, facing: 'up' } },
      { id: 'd', speaker: ZHAO, text: '[dev] 分支测试：你还有什么想问的？', choices: [
        { text: '[dev] 选项甲（setMark asked:a）', next: 'e1', setMark: 'asked:a' },
        { text: '[dev] 选项乙（setMark asked:b）', next: 'e2', setMark: 'asked:b' },
        { text: '[dev] 没什么了。', next: 'end' },
      ] },
      { id: 'e1', speaker: ZICHAO, text: '老师，我们去哪里找旱龟呢？', next: 'f', emote: { actor: ZICHAO, emote: 'question' } },
      { id: 'e2', speaker: ZICHAO, text: '[dev] 选项乙之后的台词。', next: 'f', emote: { actor: ZICHAO, emote: 'sigh' } },
      { id: 'f', speaker: ZICHAO, text: '[dev] hasFlag LETTER_OPEN 条件分支：这一句只在旗标 LETTER_OPEN 已设时出现。', condition: { hasFlag: 'LETTER_OPEN' }, elseNext: 'end', next: 'end' },
      { id: 'end', text: '[dev] 两人沿着码头向前走去。（旁白，无说话人；此节点 setFlag LETTER_OPEN）', setFlag: 'LETTER_OPEN', sfx: 'sfx-footstep-sand' },
    ],
  },
  {
    id: 'showcase-cine', start: 'a',
    nodes: [
      { id: 'a', speaker: ZICHAO, text: '老师，我们去哪里找旱龟呢？', next: 'b' },
      { id: 'b', speaker: ZHAO, text: '我们先去找个地方歇歇，顺便问问那里的人。一会儿就下雨了。', setMark: 'cine:said' },
    ],
  },
  {
    id: 'showcase-cine-end', start: 'a',
    nodes: [{ id: 'a', text: '远远就能看见大海龟旅店。' }],
  },
  {
    id: 'showcase-memory-line', start: 'a',
    nodes: [
      { id: 'a', speaker: ZICHAO, text: '不过我是一个勇敢的男人，在颠簸的甲板上我仍然稳稳的站着，看着水手们控制着帆。', next: 'b' },
      { id: 'b', text: '[dev] 回忆舞台：Esc / X 跳过，结果会返回给调用场景。' },
    ],
  },
];

export const SHOWCASE_TIMELINES: TimelineData[] = [
  {
    id: 'showcase-scene', skippable: true,
    steps: [
      { op: 'fade', out: true, ms: 250 },
      { op: 'weather', state: 'sultry', ms: 0 },
      { op: 'parallel', steps: [
        { op: 'move', actor: ZICHAO, x: 150, y: 200 },
        { op: 'move', actor: ZHAO, x: 260, y: 200 },
      ] },
      { op: 'fade', out: false, ms: 250 },
      { op: 'parallel', steps: [
        { op: 'move', actor: ZICHAO, x: 230, y: 190, speed: 70 },
        { op: 'move', actor: ZHAO, x: 330, y: 180, speed: 60 },
      ] },
      { op: 'face', actor: ZICHAO, facing: 'right' },
      { op: 'face', actor: ZHAO, facing: 'left' },
      { op: 'say', dialogue: 'showcase-cine' },
      { op: 'camera', x: 340, y: 150, ms: 600 },
      { op: 'weather', state: 'drizzle', ms: 1200 },
      { op: 'emote', actor: ZHAO, emote: 'look-up', ms: 500 },
      { op: 'wait', ms: 700 },
      { op: 'sfx', key: 'thunder' },
      { op: 'camera', shake: { ms: 300, intensity: 0.004 } },
      { op: 'label', id: 'loop' },
      { op: 'setMark', mark: 'cine:seen' },
      { op: 'goto', label: 'after', ifMark: 'cine:seen' },
      { op: 'setMark', mark: 'cine:never' },
      { op: 'label', id: 'after' },
      { op: 'music', key: 'music-harbor', fadeMs: 500 },
      // Nested timeline (own skip scope): Esc/X here skips only the tail, the outer scene finishes normally.
      { op: 'play', timeline: 'showcase-scene-tail' },
      { op: 'setFlag', flag: 'LETTER_OPEN' },
    ],
  },
  {
    id: 'showcase-scene-tail', skippable: true,
    steps: [
      { op: 'move', actor: ZHAO, x: 400, y: 150, speed: 90 },
      { op: 'move', actor: ZICHAO, x: 380, y: 170, speed: 90 },
      { op: 'camera', x: 320, y: 180, ms: 500, follow: false },
      { op: 'say', dialogue: 'showcase-cine-end' },
      { op: 'setMark', mark: 'cine:tail' },
    ],
  },
  {
    id: 'showcase-memory', skippable: true,
    steps: [
      { op: 'fade', out: false, ms: 300 },
      { op: 'move', actor: ZICHAO, x: 300, y: 220, speed: 60 },
      { op: 'camera', shake: { ms: 400, intensity: 0.006 } },
      { op: 'say', dialogue: 'showcase-memory-line' },
      { op: 'wait', ms: 400 },
      { op: 'setMark', mark: 'memory:seen' },
    ],
  },
];
